/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useMemo } from "react";
import {
  AnimatedAxis,
  BarSeries,
  BarStack,
  Grid,
  Tooltip,
  XYChart,
} from "@visx/xychart";
import dayjs from "dayjs";
import { When } from "react-if";
import { theme, Text } from "@nordcloud/gnui";
import {
  Granularity,
  TimePoint,
  CostVsBudgetForecastChartTimePoint,
} from "~/components/Charts/CostAnalysis/types";
import {
  calculateMaxDomain,
  getOuterBarPadding,
  getInnerBarPadding,
  calculateMinDomain,
} from "~/components/Charts/utils";
import { BrickLoader } from "~/components/Loaders";
import { ChartDataDisplayWrapper } from "~/components/NoData";
import { LoaderWrap } from "~/components/Utils";
import { dateFormat } from "~/constants";
import { useCurrency } from "~/hooks";
import { formatMoneyLabel, formatMoneyWithoutSymbol, noop } from "~/tools";
import {
  BudgetArea,
  CostVsBudgetLegend,
  renderCostVsBudgetTooltip,
} from "./components";
import { getNoDataMessage } from "./helpers";
import { useCostForecast } from "./hooks";
import {
  generateTimeTicks,
  chartTheme,
  getCostVsBudgetChartBarColor,
  getCostVsBudgetChartForecastBarColor,
  isCurrentMonth,
} from "./utils";

type Props = {
  width: number;
  height: number;
  startDate: string;
  endDate: string;
  loading: boolean;
  granularity: Granularity;
  data: TimePoint[];
  forecastValue: number;
  budgetData?: TimePoint[];
};

const valueTicks = 10;
const tickLength = 6;
const chartHeightRate = 1.5;
const chartHeightRateWithForecast = 1.1;
const fontSize = 10;

export function CostVsBudgetChart({
  startDate,
  endDate,
  granularity,
  width,
  height,
  data,
  loading = true,
  forecastValue,
  budgetData,
}: Props) {
  const { currency } = useCurrency();

  const {
    yData,
    xDates,
    hasNoData,
    isCostsBelowCent,
    dataWithForecast,
    forecastValues,
    budgetDisplayData,
  } = useCostForecast({
    data,
    budgetData,
    forecastValue,
    granularity,
    startDate,
    endDate,
  });

  const budgetValues =
    budgetData?.flatMap(({ value }) =>
      Number(formatMoneyWithoutSymbol(value))
    ) ?? [];

  const hasBudget = useMemo(
    () => budgetData?.some(({ value }) => value !== 0),
    [budgetData]
  );

  const heightRate = forecastValues.some((value) => value > 0)
    ? chartHeightRateWithForecast
    : chartHeightRate;

  const yMax =
    Math.ceil(
      Math.max(
        ...dataWithForecast.flatMap(({ value }) => value),
        ...forecastValues,
        ...budgetValues
      )
    ) * heightRate;

  const timeTicks = useMemo(
    () => generateTimeTicks(startDate, endDate, granularity),
    [startDate, endDate, granularity]
  );

  const innerPadding = getInnerBarPadding(xDates.length, width);
  const outerPadding = getOuterBarPadding(xDates.length);

  const allValues = [
    ...yData.map(({ value }) => value),
    ...forecastValues,
    ...budgetValues,
  ];

  const maxDomain = calculateMaxDomain(allValues, hasNoData);
  const minDomain = calculateMinDomain(allValues, hasNoData);

  return (
    <LoaderWrap
      loading={loading}
      Component={BrickLoader}
      viewBox="0 0 6 1"
      backgroundColor={theme.color.background.ui01}
    >
      <div
        css={{
          position: "relative",
          marginBottom: theme.spacing.spacing04,
        }}
      >
        <ChartDataDisplayWrapper
          hasData={!hasNoData}
          messageComponent={getNoDataMessage(isCostsBelowCent, granularity)}
        >
          <svg
            width="100%"
            height={height}
            css={{ marginTop: theme.spacing.spacing04 }}
          >
            <XYChart
              height={height}
              width={width}
              captureEvents={false}
              theme={chartTheme}
              xScale={{
                type: "band",
                paddingInner: innerPadding,
                paddingOuter: outerPadding,
              }}
              yScale={{
                type: "linear",
                nice: true,
                domain: [minDomain, maxDomain],
              }}
            >
              <Grid
                key="grid"
                rows
                columns={false}
                numTicks={valueTicks}
                strokeDasharray="1,3"
                strokeWidth={1}
                top={0}
                left={50}
              />
              <AnimatedAxis
                key="value-axis"
                hideZero
                orientation="left"
                numTicks={valueTicks}
                tickLength={tickLength}
                stroke={theme.color.border.focus}
                tickStroke={theme.color.border.focus}
                animationTrajectory="min"
                tickFormat={(n: number) => formatMoneyLabel(n, currency)}
                tickLabelProps={() => ({
                  fill: theme.color.text.text02,
                  fontFamily: "inherit",
                  fontSize,
                  textAnchor: "end",
                  width: 60,
                })}
              />
              <AnimatedAxis
                key="time-axis"
                orientation="bottom"
                tickLength={tickLength}
                stroke={theme.color.border.focus}
                strokeWidth={1}
                tickValues={timeTicks}
                // @ts-expect-error TODO: fix
                ticksComponent={() => Text}
                animationTrajectory="min"
                rangePadding={0.5}
                tickFormat={(date: string) =>
                  granularity === "days"
                    ? dayjs(date).format(dateFormat.dayMonthShort)
                    : dayjs(date).format(dateFormat.monthYearShortNoComma)
                }
                tickLabelProps={() => ({
                  fill: theme.color.text.text02,
                  fontSize,
                  fontFamily: "inherit",
                  textAnchor: "middle",
                })}
              />
              <When condition={hasBudget}>
                <BudgetArea yData={budgetDisplayData} yMax={yMax} />
              </When>
              <BarStack
                offset="diverging"
                onPointerMove={noop}
                onPointerOut={noop}
              >
                <BarSeries
                  dataKey="cost"
                  data={dataWithForecast}
                  xAccessor={({ date }) => date}
                  yAccessor={(bar) => bar.value}
                  colorAccessor={(value: CostVsBudgetForecastChartTimePoint) =>
                    getCostVsBudgetChartBarColor(value, hasBudget ?? false)
                  }
                  onPointerMove={noop}
                  onPointerOut={noop}
                />
                <BarSeries
                  dataKey="forecast"
                  data={dataWithForecast}
                  xAccessor={({ date }) => date}
                  yAccessor={(bar) =>
                    granularity === Granularity.MONTHS &&
                    isCurrentMonth(bar.date)
                      ? bar.forecast - bar.value
                      : bar.forecast
                  }
                  colorAccessor={(value: CostVsBudgetForecastChartTimePoint) =>
                    getCostVsBudgetChartForecastBarColor(value, granularity)
                  }
                  onPointerMove={noop}
                  onPointerOut={noop}
                />
              </BarStack>
              <style>{`
              .visx-bar:hover {
                opacity: ${theme.opacity};
              }
            `}</style>
              <Tooltip<CostVsBudgetForecastChartTimePoint>
                detectBounds
                snapTooltipToDatumX
                snapTooltipToDatumY
                renderTooltip={({ tooltipData }) =>
                  renderCostVsBudgetTooltip({
                    currency,
                    tooltipDateFormat:
                      granularity === Granularity.MONTHS
                        ? dateFormat.monthShort
                        : dateFormat.dayMonthShort,
                    hasBudget: hasBudget ?? false,
                    tooltipData,
                  })
                }
              />
            </XYChart>
          </svg>
          <CostVsBudgetLegend />
        </ChartDataDisplayWrapper>
      </div>
    </LoaderWrap>
  );
}
